import { useCallback, useEffect, useState } from 'react';
import { propsFundoModal } from '../lib/backdropModal';

interface OpcoesFechamentoSeguro {
    /** Modal aberto. Serve só para zerar o estado interno quando ele abre de novo. */
    isOpen: boolean;
    /** Há preenchimento a perder? Com `false` o modal fecha direto, sem perguntar. */
    temRascunho: boolean;
    /** Fechamento de verdade — o `onClose` do modal. */
    onFechar: () => void;
}

/**
 * Impede que um formulário longo feche por acidente e leve junto o que já foi digitado.
 *
 * Junta as duas travas que os modais de documento jurídico precisam:
 *
 *  1. **Gesto** — de `propsFundoModal`: só fecha quando o mousedown e o clique
 *     aconteceram no fundo. Selecionar texto num campo e soltar o mouse fora não
 *     conta mais como clique fora.
 *  2. **Confirmação** — clique fora de verdade (ou o ✕, ou o Cancelar) com campos
 *     preenchidos pergunta antes de descartar.
 *
 * Nasceu do chamado "Fechando a tela": a Notificação Extrajudicial e o Distrato
 * fechavam sozinhos no meio do preenchimento. Modais sem formulário longo não
 * precisam da segunda trava — para eles basta `propsFundoModal` direto.
 *
 * Usa hooks, então chame antes do `if (!isOpen) return null;` do modal.
 */
export function useFechamentoSeguro({ isOpen, temRascunho, onFechar }: OpcoesFechamentoSeguro) {
    const [confirmandoDescarte, setConfirmandoDescarte] = useState(false);

    useEffect(() => {
        if (!isOpen) setConfirmandoDescarte(false);
    }, [isOpen]);

    /** Ponto único de saída: use no ✕, no Cancelar e no clique fora. */
    const pedirParaFechar = useCallback(() => {
        if (temRascunho) setConfirmandoDescarte(true);
        else onFechar();
    }, [temRascunho, onFechar]);

    const descartarEFechar = useCallback(() => {
        setConfirmandoDescarte(false);
        onFechar();
    }, [onFechar]);

    const continuarPreenchendo = useCallback(() => setConfirmandoDescarte(false), []);

    /** Espalhe no <div> do fundo escuro: `{...propsDoFundo}`. */
    const propsDoFundo = propsFundoModal(pedirParaFechar);

    return { propsDoFundo, pedirParaFechar, confirmandoDescarte, descartarEFechar, continuarPreenchendo };
}

export default useFechamentoSeguro;
